import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    TextField,
    MenuItem
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import supplierOrdersService from '../services/supplierOrdersService';
import suppliersService from '../services/suppliersService';

const getStatusColor = (status) => {
    switch (status) {
        case 'draft': return 'default';
        case 'ordered': return 'primary';
        case 'received': return 'success';
        case 'cancelled': return 'error';
        default: return 'default';
    }
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'draft': return 'Brouillon';
        case 'ordered': return 'Commandée';
        case 'received': return 'Reçue';
        case 'cancelled': return 'Annulée';
        default: return status;
    }
}

const SupplierOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await supplierOrdersService.getAll();
            setOrders(data);
        } catch (error) {
            console.error('Erreur chargement commandes:', error);
            alert('Erreur au chargement des commandes');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Voulez-vous vraiment supprimer cette commande ?')) {
            try {
                await supplierOrdersService.delete(id);
                loadData();
            } catch (error) {
                console.error(error);
                alert('Erreur lors de la suppression');
            }
        }
    };

    const handleDownloadPdf = async (id) => {
        try {
            const blob = await supplierOrdersService.downloadPdf(id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `commande_fournisseur_${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('Erreur téléchargement PDF:', error);
            alert('Erreur lors du téléchargement du PDF');
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredOrders = orders.filter(o =>
        o.supplier?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.id.toString().includes(searchTerm)
    );

    const paginatedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) return <LoadingSpinner />;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    Commandes Fournisseurs
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/supplier-orders/new')}
                >
                    Nouvelle Commande
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Box sx={{ p: 2 }}>
                    <TextField
                        label="Rechercher (Fournisseur, ID)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Fournisseur</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Date prévue</TableCell>
                            <TableCell>Montant Total</TableCell>
                            <TableCell>Date création</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedOrders.map((order) => (
                            <TableRow key={order.id}>
                                <TableCell>#{order.id}</TableCell>
                                <TableCell>{order.supplier?.name || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={getStatusLabel(order.status)}
                                        color={getStatusColor(order.status)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {order.expectedAt ? new Date(order.expectedAt).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell>{order.totalAmount} €</TableCell>
                                <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        color="primary"
                                        onClick={() => navigate(`/supplier-orders/${order.id}`)}
                                        size="small"
                                    >
                                        <VisibilityIcon />
                                    </IconButton>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleDownloadPdf(order.id)}
                                        size="small"
                                        title="Télécharger le PDF"
                                    >
                                        <PdfIcon />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDelete(order.id)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {paginatedOrders.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">Aucune commande trouvée</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredOrders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Box>
    );
};

export default SupplierOrders;
